import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IApikey,
  ICliente,
  ICreateApikey,
  IQueryParam,
  IUpdateApikey,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { ApikeysService } from '../apikeys.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

@Component({
  selector: 'app-crear-editar-apikey',
  templateUrl: './crear-editar-apikey.component.html',
  styleUrl: './crear-editar-apikey.component.scss',
  standalone: false,
})
export class CrearEditarApikeyComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  private service = inject(ApikeysService);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  /** Abierto como modal desde el listado; por ruta (compatibilidad) no hay */
  data = inject<{ edit?: IApikey }>(MAT_DIALOG_DATA, { optional: true });
  dialogRef? = inject<MatDialogRef<CrearEditarApikeyComponent>>(MatDialogRef, {
    optional: true,
  });

  public clientePropio = LoginService.getCliente();
  public id: string | null = null;
  public title = '';
  public apikey?: IApikey;

  public form?: FormGroup;

  private async listar() {
    this.apikey = await firstValueFrom(this.lista2.apikey(this.id!));
  }

  private createForm() {
    this.form = new FormGroup(
      {
        identificacion: new FormControl(
          this.apikey?.identificacion,
          Validators.required,
        ),
        global: new FormControl(
          this.apikey?.global || false,
          Validators.required,
        ),
        idClientes: new FormControl(this.apikey?.idClientes || []),
      },
      { validators: this.clientesValidator() },
    );
  }

  private clientesValidator(): any {
    return (group: FormGroup): ValidationErrors | null => {
      // NULL si es valido
      const clientes = group.get('idClientes')?.value as string[];
      const global = group.get('global')?.value as boolean;
      // Sino es global y no tiene clientes es invalido
      const forbidden = !global && (!clientes || clientes.length === 0);
      return forbidden
        ? { forbiddenIdClientes: { value: group.get('idClientes')?.value } }
        : null;
    };
  }

  private getData() {
    const data: ICreateApikey | IUpdateApikey = this.form?.value;
    return data;
  }

  // Actions

  public async onSubmit() {
    const res = await this.dialogService.confirm(
      `${this.title} Apikey`,
      `¿Querés ${this.title.toLowerCase()} la apikey?`,
    );
    if (!res) return;
    try {
      if (this.id) {
        // Update
        const update = this.getData() as IUpdateApikey;
        if (!update) return;
        await this.service.update(this.id, update);
        this.helper.notifSuccess('Apikey Editada');
        this.cerrarCon(update);
      } else {
        // Create
        const create = this.getData() as ICreateApikey;
        if (!create) return;
        await this.service.create(create);
        this.helper.notifSuccess('Apikey Creada');
        this.cerrarCon(create);
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  /** Modal: cierra con resultado (el listado refresca); página: vuelve */
  private cerrarCon(resultado: ICreateApikey | IUpdateApikey) {
    if (this.dialogRef) this.dialogRef.close(resultado);
    else this.helper.volver();
  }

  public volver(): void {
    if (this.dialogRef) this.dialogRef.close();
    else this.helper.volver();
  }

  // Hooks
  async ngOnInit() {
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id') || this.data?.edit?._id || null;
    this.title = this.id ? 'Editar' : 'Crear';
    if (this.id) {
      await this.listar();
    }
    this.createForm();
  }
}
